import { useMemo } from 'react';
import { useLocation } from 'react-router';

/**
 * Serialize an object to a query string.
 * default behavior: keys sorted alphabetically, undefined values dropped, arrays
 * as repeated keys (`k=a&k=b`).
 */
export const stringifyParams = (
    params: Record<string, string | string[] | undefined>,
): string => {
    const searchParams = new URLSearchParams();
    Object.keys(params)
        .sort((a, b) => a.localeCompare(b))
        .forEach((key) => {
            const value = params[key];
            if (value === undefined) return;
            const values = Array.isArray(value) ? value : [value];
            values.forEach((v) => searchParams.append(key, v));
        });
    return searchParams.toString();
};

/**
 * On a given string url, applies given transformation treatment (e.g. snakify)
 * on parts of the url indicated by the "indexes" array, parts of the URL being
 * determined by a split on the "/"
 */
export const transformUrl = (
    url: string,
    indexes: number[],
    transformCallback: (arg: string) => string,
): string =>
    url
        .split('/')
        .map((pathElem, index) =>
            indexes.includes(index) ? transformCallback(pathElem) : pathElem,
        )
        .join('/');

/**
 * On the given URL, applies a treatement (e.g. snakify) to all the values of the specified queryParams.
 * E.G : snakify all values for the queryparams key "metric" and "dimension"
 * This helper is mainly (but not exclusively) used by the getDataUrlMiddleware helper.
 */
export const transformUrlParams = (
    endpoint: string,
    paramsKeysToTransform: Array<string>,
    transformMethod: (text: string) => string,
): string => {
    const [url, queryParams] = endpoint.split('?');
    const splittedParams: Record<string, string[]> = {};
    new URLSearchParams(queryParams).forEach((value, key) => {
        if (!splittedParams[key]) splittedParams[key] = [];
        splittedParams[key].push(value);
    });

    Object.keys(splittedParams).forEach((key) => {
        if (paramsKeysToTransform.includes(key)) {
            splittedParams[key] = splittedParams[key].map((value) => {
                const [field, ...rest] = value.split('|');
                return [transformMethod(field), ...rest].join('|');
            });
        }
    });

    return `${url}?${stringifyParams(splittedParams)}`;
};

export function useGetSearchParam(key: string, defaultValue: string): string;
export function useGetSearchParam(
    key: string,
    defaultValue?: string,
): string | undefined {
    const location = useLocation();
    const urlParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    return urlParams.get(key) ?? defaultValue;
}

export const getPublicToken = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get('public_token');
};

/**
 * Return a string with the public_token parameter if there is a publicToken.
 */
export const buildPublicTokenSearchParams = (
    publicToken: string | undefined,
) => {
    if (!publicToken) return '';
    return new URLSearchParams({ public_token: publicToken }).toString();
};
